import React, {FC, memo} from 'react';
import {withStyles} from '@material-ui/core';
import MUIList from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';

import {getDirectionStyles} from '../../utils/ThemeUtils';
import MultipleOption, {MultipleOptionProps} from './MultipleOption';

import {Option} from '../../types';

const StyledListItem = withStyles(theme => ({
    root: {
        alignItems: 'flex-start',
        padding: getDirectionStyles(theme.direction)(
            theme.spacing(2, 2, 2, 4),
            theme.spacing(2, 4, 2, 2)
        ),

        '& .MuiTypography-root': {
            height: 'max-content'
        }
    }
}))(ListItem);

export interface ListProps {
    options: Option[];
    value: Option[];
    label: string;
    onChange: (event: React.MouseEvent, options: Option[]) => void;
    overrideMultipleOptionProps?: (option: Option) => Partial<MultipleOptionProps>;
}

const List: FC<ListProps> = ({
    options,
    value,
    label,
    onChange,
    overrideMultipleOptionProps
}: ListProps) => {
    const selectedValues = value.map(({value}) => value);
    const isSelectedAll = value.length === options.length;

    const handleClick = (option: Option) => (event: React.MouseEvent) => {
        const newValue = value.includes(option)
            ? value.filter(opt => opt !== option)
            : [...value, option];

        onChange(event, newValue);
    };

    const handleSelectAll = (event: React.MouseEvent) => {
        onChange(event, value.length ? [] : [...options]);
    };

    return (
        <MUIList component="nav" aria-label="main mailbox folders">
            <StyledListItem button onClick={handleSelectAll} key="select-all">
                <MultipleOption
                    selected={isSelectedAll}
                    label={label}
                    CheckboxProps={{
                        indeterminate: !isSelectedAll && Boolean(value.length)
                    }}
                />
            </StyledListItem>
            {options.map(option => {
                const {label, secondaryText, value} = option;
                const selected = selectedValues.includes(value);
                const overrides = overrideMultipleOptionProps
                    ? overrideMultipleOptionProps(option)
                    : {};

                return (
                    <StyledListItem
                        key={value + secondaryText}
                        button
                        onClick={handleClick(option)}>
                        <MultipleOption
                            secondaryText={secondaryText}
                            label={label}
                            value={value}
                            selected={selected}
                            {...overrides}
                        />
                    </StyledListItem>
                );
            })}
        </MUIList>
    );
};

export default memo(List);
